import React, {useRef, useState, useEffect} from "react";
import {Box, CircularProgress, IconButton, Stack, Tooltip} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {PauseIcon, PlayIcon} from "@hugeicons/core-free-icons";
import {createAudioLoader, ensureAudioSource, isAbortError} from "../../../../utils/audioLoader";

// Small play / progress / duration player. request({signal}): repository call returning the audio blob (see
// utils/audioLoader), null when there is no audio. audioKey: identifies the audio (e.g. "conversation-42"), the
// player starts over only when it changes. The file is downloaded on the first play.
const CompactAudioPlayer = ({audioKey, request}) => {
    const audioRef = useRef(null);
    const loaderRef = useRef(null);
    const requestRef = useRef(request);
    requestRef.current = request;
    const hasAudio = Boolean(request);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [progress, setProgress] = useState(0); // 0-100
    const [duration, setDuration] = useState(0);

    const formatDuration = (seconds) => {
        if (!seconds || !isFinite(seconds) || isNaN(seconds)) return "0s";
        const total = Math.round(seconds);
        const mins = Math.floor(total / 60);
        const secs = total % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    // another audio gets a new loader
    useEffect(() => {
        if (!hasAudio) return;
        const loader = createAudioLoader((options) => requestRef.current(options));
        loaderRef.current = loader;
        setIsPlaying(false);
        setFailed(false);
        setProgress(0);
        setDuration(0);
        return () => {
            audioRef.current?.pause();
            audioRef.current?.removeAttribute("src");
            loader.dispose();
        };
    }, [audioKey, hasAudio]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => {
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
            }
        };
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
        };

        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("ended", onEnded);

        return () => {
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("loadedmetadata", onLoadedMetadata);
            audio.removeEventListener("ended", onEnded);
        };
    }, [audioKey, hasAudio]);

    const togglePlay = async () => {
        const audio = audioRef.current;
        if (!audio || !hasAudio) return;
        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }
        try {
            setLoading(true);
            if (!(await ensureAudioSource(audio, loaderRef.current))) return;
            await audio.play();
            setIsPlaying(true);
            setFailed(false);
        } catch (error) {
            if (!isAbortError(error)) {
                console.error("Failed to play audio:", error);
                setFailed(true);
            }
            setIsPlaying(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newProgress = (clickX / rect.width) * 100;
        audio.currentTime = (newProgress / 100) * duration;
        setProgress(newProgress);
    };

    if (!hasAudio) {
        return (
            <Stack direction="row" alignItems="center" gap={0.75} sx={{width: 124}}>
                <IconButton size="small" disabled sx={{
                    width: 28, height: 28, bgcolor: "#F2F2F7", "&.Mui-disabled": {color: "#B4B4C3"},
                }}>
                    <HugeiconsIcon icon={PlayIcon} size={13} strokeWidth={2}/>
                </IconButton>
            </Stack>
        );
    }

    return (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{width: 124}}>
            <audio ref={audioRef} preload="metadata" style={{display: "none"}}/>

            <Tooltip title={failed ? "Audio not available" : ""}>
                <span>
                    <IconButton size="small" disabled={loading} onClick={(e) => {
                        e.stopPropagation();
                        togglePlay();
                    }} sx={{
                        width: 28, height: 28, flexShrink: 0, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.2)",
                        "&:hover": {bgcolor: "rgba(132, 145, 247, 0.3)"}, ...(failed && {color: "#C73A3A", bgcolor: "#FDECEC"}),
                    }}>
                        {loading
                            ? <CircularProgress size={12} sx={{color: "#4B55C9"}}/>
                            : isPlaying ? <HugeiconsIcon icon={PauseIcon} size={13}/> : <HugeiconsIcon icon={PlayIcon} size={13} strokeWidth={2}/>}
                    </IconButton>
                </span>
            </Tooltip>

            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    handleSeek(e);
                }}
                sx={{
                    position: "relative",
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#E4E4EE",
                    cursor: "pointer",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: `${progress}%`,
                        borderRadius: 2,
                        bgcolor: "#8491F7",
                    }}
                />
            </Box>
            <Box component="span" sx={{fontSize: 11, fontWeight: 600, color: "#8A8AA0", minWidth: 32, textAlign: "right", fontVariantNumeric: "tabular-nums"}}>
                {formatDuration(duration)}
            </Box>
        </Stack>
    );
};

export default CompactAudioPlayer;
